import { Component } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { debounceTime, switchMap, map} from 'rxjs/operators';
import { of } from 'rxjs';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { faFile } from '@fortawesome/free-regular-svg-icons';
import { throwToolbarMixedModesError } from '@angular/material/toolbar';
import { ToastrService } from 'ngx-toastr';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

@Component({
  selector: 'app-update-rates',
  templateUrl: './update-rates.component.html',
  styleUrls: ['./update-rates.component.scss']
})

export class UpdateRatesComponent {
  updateURL = `api/rates/batch_update_rates.php`
  csv_headers = ['carrier_contact_uuid', 'rate_method', 'rate_unit', 'start_date', 'end_date', 'measurement_start', 'measurement_end', 'rate', 'min_rate', 'origin_postal_code', 'origin_city', 'origin_region', 'destination_postal', 'destination_city', 'destination_region', 'transit_days'];
  isDraggingOverDocument: boolean = false;
  faTrash = faTrash;
  faFile = faFile;

  updateForm: FormGroup;
  rate_metrics = ["WEIGHT", "LENGTH"];
  rate_units = ["POUNDS", "FEET", "KILOGRAMS", "METERS"];
  currencies = ["USD", "CAD"];

  filteredCarrierList: any[] = [];
  filteredCityList: any[] = [];

  // for dynamic form field labels
  measurement_type = 'Measurement';
  measurement_unit = '';

  constructor(private fb: FormBuilder, private http: HttpClient, private toastr: ToastrService){
    this.updateForm = this.fb.group({
      contact_uuid: ['', Validators.required],
      contact_name: ['', Validators.required],
      start_date: [new Date(), Validators.required],
      end_date: [new Date(), Validators.required],
      rate_metric: ['', Validators.required], //length, width
      rate_unit: ['', Validators.required], //lbs, ft, etc
      currency: ['', Validators.required], //USD, CAD
      routes: this.fb.array([])
    })
    this.addRoute();
  }
  

  // TO DO: MAKE THE VALIDATORS CONDITIONAL
  addRoute(): void {
    const routeGroup = this.fb.group({
      origin_city: ['', Validators.required],
      origin_region: ['', Validators.required],
      origin_postal: [''],  // Not required initially, handled by validator
      destination_city: ['', Validators.required],
      destination_region: ['', Validators.required],
      destination_postal: [''],  // Not required initially, handled by validator
      min_rate: [0.0],
      transit_days: [0],
      measurement_categories: this.fb.array([]),
      filteredOriginCities: [[]],  // Unique filtered list for origin
      filteredDestinationCities: [[]] // Unique filtered list for destination
    }, { validators: cityStateOrPostalValidator() });

    // Add the route group to the routes array
    (this.updateForm.get('routes') as FormArray).push(routeGroup);
    this.add_measurement_category(this.routes.length - 1);

    // Autocomplete for origin_city
    routeGroup.get('origin_city')?.valueChanges.pipe(
      debounceTime(300),
      switchMap(value => {
        if (value && value.length > 0) {
          return this.searchCities(value);  // Using searchCities for origin city
        } else {
          return of([]);
        }
      })
    ).subscribe(suggestions => {
      routeGroup.get('filteredOriginCities')?.setValue(suggestions);  // Store suggestions in the form group
    });

    // Autocomplete for destination_city (also using searchCities now)
    routeGroup.get('destination_city')?.valueChanges.pipe(
      debounceTime(300),
      switchMap(value => {
        if (value && value.length > 0) {
          return this.searchCities(value);  // Using searchCities for destination city
        } else {
          return of([]);
        }
      })
    ).subscribe(suggestions => {
      routeGroup.get('filteredDestinationCities')?.setValue(suggestions);  // Store suggestions in the form group
    });

}

  removeRoute(index: number): void{
    (this.updateForm.get('routes') as FormArray).removeAt(index);
  }

  get routes(): FormArray{
    return this.updateForm.get('routes') as FormArray;
  }

  get_measurement_categories(route_index: number): FormArray{
    return this.routes.at(route_index).get('measurement_categories') as FormArray;
  }

  add_measurement_category(route_index: number){
    const category_group = this.fb.group({
      measurement_start: [0.0, Validators.required],
      measurement_end: [0.0, Validators.required],
      rate: [0.0, Validators.required]
    })
    this.get_measurement_categories(route_index).push(category_group);
  }

  remove_measurement_category(route_index: number, measurement_category_index: number): void {
    this.get_measurement_categories(route_index).removeAt(measurement_category_index);
  }

  onSubmit(): void{ 
    // got some interfaces for Route and Category
    let postBody: PostBody = {
      upload_type: 'form',
      carrier_contact_uuid: this.updateForm.get('contact_uuid')?.value,
      currency: this.updateForm.get('currency')?.value,
      start_date: this.formatDate(this.updateForm.get('start_date')?.value),
      end_date: this.formatDate(this.updateForm.get('end_date')?.value),
      rate_metric: this.updateForm.get('rate_metric')?.value,
      rate_unit: this.updateForm.get('rate_unit')?.value,
      routes: [],
    }
    // populate the routes
    this.routes.controls.forEach((group: AbstractControl<any,any>,index: number) => {
      const formGroup = group as FormGroup;
      let postBodyRoute: Route = {
        transit_days: formGroup.get('transit_days')?.value,
        min_rate: formGroup.get('min_rate')?.value,
        categories: []
      };

      // set origin
      if(formGroup.get('origin_postal')?.value !== ""){
        postBodyRoute['origin_postal'] = formGroup.get('origin_postal')?.value;
      }
      else{
        postBodyRoute['origin_city'] = formGroup.get('origin_city')?.value,
        postBodyRoute['origin_region'] = formGroup.get('origin_region')?.value
      }

      // set destination
      if(formGroup.get('destination_postal')?.value !== ""){
        postBodyRoute['destination_postal'] = formGroup.get('destination_postal')?.value;
      }
      else{
        postBodyRoute['destination_city'] = formGroup.get('destination_city')?.value,
        postBodyRoute['destination_region'] = formGroup.get('destination_region')?.value
      }

      // set categories
      this.get_measurement_categories(index).controls.forEach((categoryGroup: AbstractControl<any,any>, categoryIndex: number) => {
        const categoryFormGroup = categoryGroup as FormGroup;
        let postBodyCategory: Category = {
          rate: categoryFormGroup.get('rate')?.value,
          measurement_start: categoryFormGroup.get('measurement_start')?.value,
          measurement_end: categoryFormGroup.get('measurement_end')?.value
        }
        postBodyRoute['categories'].push(postBodyCategory);
      })

      // add route to post body
      postBody['routes'].push(postBodyRoute);
      console.log(JSON.stringify(postBody));

      this.http.post<any>(this.updateURL, postBody).subscribe(
        (response) => {
          alert(this.formatSubmissionResponse(response));
        },
        (error) => {
          alert(error.message)
          console.error(error)
        }
      )
    })

  }

  onCitySelected(event: any, location_type: string, index: number) {
    const routes = this.updateForm.get('routes') as FormArray;
    const routeGroup = routes.at(index) as FormGroup;

    // Get the selected city object directly from the event
    const selectedCity = event.option.value;

    if (selectedCity) {
        if (location_type === 'origin') {
            routeGroup.get('origin_city')?.setValue(selectedCity.accentcity);
            routeGroup.get('origin_region')?.setValue(selectedCity.rc);  // Set the state
        } else if (location_type === 'destination') {
            routeGroup.get('destination_city')?.setValue(selectedCity.accentcity);
            routeGroup.get('destination_region')?.setValue(selectedCity.rc);  // Set the state
        }
    } else {
        console.error('Selected option not found');
    }
}

  /* AUTOCOMPLETE CONTACT STUFF BELOW */
  onOptionSelected(event: any): void{
    console.log('Event sent to onOptionSelected: ', event)
      const selectedOption = this.filteredCarrierList.find(option => option.contact_organization === event.option.value);
      if (selectedOption){
        this.updateForm.get('contact_uuid')?.setValue(selectedOption.contact_uuid);
      }
    
  }

  searchContacts(query: string){
    let url = `/api/autocomplete/autocomplete.php?query=${query}`;
    return this.http.get<any>(url).pipe(map(response => response.data))
  }

  searchCities(query: any){
    console.log('calling searchCities with query: ', query);
    let url = `/api/autocomplete/autocomplete_city.php?query=${query}`;
    return this.http.get<any>(url);
  }

  formatSubmissionResponse(response: any){
    let formattedResponse = '';
    if(response.unsuccessful){
      formattedResponse += "Not added:\n";
      for(const item of response.unsuccessful){
        formattedResponse += `\tRoute: ${item.route} | Category: ${item.category} | Reason: ${item.reason}\n`;
      }
    }
    if(response.successfully_added){
      for(const item of response.successfully_added){
        formattedResponse += `\tRoute: ${item.route} | Category: ${item.category}\n`;
      }
    }
    return formattedResponse;
  }

  /*FUNCTIONS TO HANDLE DRAG/DROP CSV FEATURE BELOW*/

  createTemplateCSV(){
    let csvContent = this.csv_headers.join(',') + '\n';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8'});
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'carrier_rate_update_template.csv');
    document.body.appendChild(link);
    link.click();
  }

  // Handle when files are dragged over the dropzone
  handleDocumentDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOverDocument = true;
  }

  // Handle when files leave the dropzone
  handleDocumentDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOverDocument = false;
  }

  handleCSVUpload(event: DragEvent){
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOverDocument = false;

    console.log(event);

    if(event.dataTransfer?.files.length){
      const files = event.dataTransfer.files;
      const formData = new FormData();

      for (let i = 0; i < files.length; i++){
        formData.append('file', files[i], files[i].name);
        // check if csv file
        if (files[i].name.endsWith('.csv')){
          const fileReader = new FileReader();
          fileReader.onload = (e: any) => {
            let csvContent = e.target.result;

            const isDataValid = this.checkFileData(csvContent);
            console.log('is data valid? ', isDataValid);
            if (isDataValid) {
              csvContent = this.trimFileData(csvContent);
              let postData = {
                data: this.parseCSVtoJSON(csvContent),
                upload_type: 'csv'
              }
              console.log('post data: ', postData)
              this.http.post(this.updateURL, postData).subscribe(
                (response) => {
                  console.log(response)
                  alert(this.formatSubmissionResponse(response))
                },
                (error) => {
                  console.error(error)
                }
              )
            }
          }
          fileReader.readAsText(files[i]);
        }
      }
    }
  }

  parseCSVtoJSON(csvContent: string){
    const rows = csvContent.split('\n').map(row => row.split(','));
    const headers = rows[0];
    const data = [];

    for (let i = 1; i < rows.length; i++) {
      const obj: any = {};
      for (let j = 0; j < headers.length; j++) {
        obj[headers[j].trim()] = rows[i][j] ? rows[i][j].trim() : null; // Trim and handle empty values
      }
      data.push(obj);
    }

    return data;
  }

  trimFileData(csvContent: string): string{
    const rows = csvContent.split('\n').map(row => row.split(','));
    for(let i = 1; i < rows.length; i++){
      if(rows[i].length == 1){ 
        rows.splice(i, 1);
      }
    }
    return rows.map(row => row.join(',')).join('\n');
  }

  checkFileData(csvContent: string): boolean{
    const rows = csvContent.split('\n').map(row => row.split(','));
    const headings = rows[0].map(h => h.trim());

    // check if headings are valid
    if (!this.checkCSVHeaders(headings)){
      return false;
    }
    // for each row, check if data is valid
    console.log('number of rows: ' , rows.length)
    for(let i = 1; i < rows.length; i++){
      if(!this.checkCSVRowData(rows[i].map(data => data.trim())) && rows[i].length != 1){ // 2nd condition deals with empty lines
        return false;
      }
    }
    return true;
  }

  // check that csv headers match those in template
  checkCSVHeaders(headers: string[]){
    if (headers.length !== this.csv_headers.length){
      return false;
    }
    for (let i = 0; i < headers.length; i++){
      if (headers[i] !== this.csv_headers[i]){
        return false;
      }
    }
    return true;
  }

  checkCSVRowData(row: string[]){
    const carrier_contact_uuid = row[0];
    const rate_method = row[1];
    const rate_unit = row[2];
    const start_date = new Date(row[3]);
    const end_date = new Date(row[4]);
    const measurement_start = parseInt(row[5]);
    const measurement_end = parseInt(row[6]);
    const rate = parseFloat(row[7]);
    const min_rate = parseFloat(row[8]);
    const origin_postal = row[9];
    const origin_city = row[10];
    const origin_region = row[11];
    const destination_postal = row[12];
    const destination_city = row[13];
    const destination_region = row[14];
    const transit_days = parseInt(row[15]);

    // check if the dates are dates
    if(isNaN(start_date.getTime()) || isNaN(end_date.getTime())) return false;

    // check if the numbers are numbers
    if (isNaN(measurement_start) || isNaN(measurement_end) || isNaN(rate) || isNaN(min_rate) || isNaN(transit_days)) return false;

    // check origin and destination data
    // either city and region OR postal should be filled
    const valid_origin_postal = origin_postal !== null && origin_postal !== '';
    const valid_origin_city = origin_city !== null && origin_city !== '';
    const valid_origin_region = origin_region !== null && origin_region.trim() !== '';
    const valid_origin = (valid_origin_city && valid_origin_region) !== valid_origin_postal;
    if (!valid_origin) return false;

    const valid_destination_postal = destination_postal !== null && destination_postal !== '';
    const valid_destination_city = destination_city !== null && destination_city !== '';
    const valid_destination_region = destination_region !== null && destination_region !== '';
    const valid_destination = (valid_destination_city && valid_destination_region) !== valid_destination_postal;
    if (!valid_destination) return false;

    // check that the other data are included
    if (carrier_contact_uuid.length == 0 || rate_method.length == 0 || rate_unit.length == 0) return false;

    // check that rate method and unit are valid strings
    const rate_methods = ['WEIGHT', 'LENGTH'];
    const rate_units = ['LBS', 'KG', 'FT', 'M'];
    if(!rate_methods.includes(rate_method)) return false;
    if(!rate_units.includes(rate_unit)) return false;

    // check regions are length 2, if applicable
    if (valid_origin_region && origin_region.length != 2) return false;
    if (valid_destination_region && destination_region.length != 2) return false;

    // check postal/zip codes are correct format, if applicable
    const postalCodeRegex = /^[A-Za-z]\d[A-Za-z][ ]?\d[A-Za-z]\d$/;
    const zipCodeRegex = /^\d{5}(?:[-\s]\d{4})?$/;
    if (valid_origin_postal && !postalCodeRegex.test(origin_postal) && !zipCodeRegex.test(origin_postal)) return false;
    if (valid_destination_postal && !postalCodeRegex.test(destination_postal) && !zipCodeRegex.test(destination_postal)) return false;

    return true;
  }


  // searchCitiesFromJson(query: string) {
  //   const firstLetter = query.charAt(0).toLowerCase();
  //   const url = `/assets/cities_${firstLetter}.json`; 
  
  //   return this.http.get<any>(url).pipe(
  //     map(response => response.cities.filter((city: any) => 
  //       city.accentcity.toLowerCase().includes(query.toLowerCase())
  //     ))
  //   );
  // }

 // Function to show the suggestion inline but NOT modify the input
  // showSuggestion(control: any, suggestion: string) {
  //   const currentInput = control.value;
  //   if (suggestion.toLowerCase().startsWith(currentInput.toLowerCase())) {
  //     // Only show the suggestion visually, do not overwrite the input field value
  //     const inputElement = document.querySelector(`input[formControlName="destination_city"]`) as HTMLInputElement;
  //     if (inputElement) {
  //       // Display the suggestion visually by showing the typed part and highlighting the suggested part
  //       inputElement.value = currentInput + suggestion.substring(currentInput.length);
  //       inputElement.setSelectionRange(currentInput.length, suggestion.length);  // Highlight the suggestion
  //     }
  //   }
  // }

  // onTabPress(event: KeyboardEvent) {
  //   console.log('Key pressed:', event.key);
  //   const targetElement = event.target as HTMLInputElement;
  //   // Ensure the event fires only on the destination_city field and when tab is hit
  //   if (targetElement && targetElement.getAttribute('formControlName') === 'destination_city') {
  //     if (event.key === 'Tab') {
  //       if (this.filteredCityList.length > 0) {
  //         event.preventDefault();  
  //         const firstSuggestion = this.filteredCityList[0];  // Get the first suggestion
  //         this.updateForm.get('destination_city')?.setValue(firstSuggestion.accentcity);  // Apply the suggestion
  //       }
  //     }
  //   }
  // }

  ngOnInit(){
    // Set autocomplete drop down list for contacts
    this.updateForm.get('contact_name')?.valueChanges.pipe(
      debounceTime(300),
      switchMap(value => {
        const searchTerm = value ?? '';
        return searchTerm.length >= 3 ? this.searchContacts(searchTerm) : [];
      })
    )
    .subscribe( options => {
      this.filteredCarrierList = options
    });


    // Set dynamic form field labels
    this.updateForm.get('rate_metric')?.valueChanges.subscribe( value => {
      console.log(`rate metric changed to ${value}`)
      if (value && value !== ''){
        this.measurement_type = this.capitalizeFirstLetter(value);
      }
      else{
        this.measurement_type = 'Measurement';
      }
    })
    this.updateForm.get('rate_unit')?.valueChanges.subscribe( value => {
      if (value){
        if(value == 'FEET'){
          this.measurement_unit = '/FT';
        }
        else if(value == 'POUNDS'){
          this.measurement_unit = '/100 LBS';
        }
        else if(value == 'KILOGRAMS'){
          this.measurement_unit = '/100 KG';
        }
        else if(value == 'METERS'){
          this.measurement_unit = '/M';
        }
        else{
          this.measurement_unit = '';
        }
      }
    })
  }

  capitalizeFirstLetter(str: string): string{
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  formatDate(d: Date): string {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(d.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }

}

export function cityStateOrPostalValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
      const originCity = group.get('origin_city')?.value;
      const originRegion = group.get('origin_region')?.value;
      const originPostal = group.get('origin_postal')?.value;

      const destinationCity = group.get('destination_city')?.value;
      const destinationRegion = group.get('destination_region')?.value;
      const destinationPostal = group.get('destination_postal')?.value;

      // Check for origin fields
      const originValid = (originCity && originRegion) || originPostal;

      // Check for destination fields
      const destinationValid = (destinationCity && destinationRegion) || destinationPostal;

      // If both origin and destination are valid, return null (no errors)
      if (originValid && destinationValid) {
          return null;
      }

      // If either origin or destination is invalid, return an error
      return { cityStateOrPostalRequired: true };
  };
}

interface PostBody{
  upload_type: string,
  carrier_contact_uuid: string,
  currency: string,
  start_date: string | Date,
  end_date: string | Date,
  rate_metric: string,
  rate_unit: string,
  routes: Route[]
}

interface Route{
  transit_days?: number,
  min_rate: number,
  origin_postal?: string,
  origin_city?: string,
  origin_region?: string,
  destination_postal?: string,
  destination_city?: string,
  destination_region?: string,
  categories: Category[]
}

interface Category{
  rate: number,
  measurement_start: number,
  measurement_end: number
}
